import { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { ChevronDown, Plus, X, Sparkles } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Masters fetched once per session and shared by every form
let mastersCache = null;
let mastersPromise = null;

export const useMasters = () => {
  const [masters, setMasters] = useState(mastersCache || {});
  useEffect(() => {
    if (mastersCache && Object.keys(mastersCache).length) return;
    mastersPromise = axios.get(`${API}/masters`).then((r) => {
      mastersCache = r.data.masters;
      return mastersCache;
    }).catch(() => ({}));
    mastersPromise.then(setMasters);
  }, []);
  return masters;
};

const ErpHint = ({ hint }) =>
  hint ? (
    <span className="ml-2 font-jbmono text-[8px] uppercase tracking-wider text-[#7C3AED]/80" title={`Zoho: ${hint.zoho} · SAP: ${hint.sap}`}>
      ⇄ {hint.zoho.split(".")[0]}·{hint.sap.split(".")[0]}
    </span>
  ) : null;

const FieldLabel = ({ label, erp }) => (
  <p className="mb-1.5 font-jbmono text-[10px] uppercase tracking-widest text-slate-500">
    {label}
    {erp?.required && <span className="ml-1 text-[#F97316]">*</span>}
    <ErpHint hint={erp} />
  </p>
);

// Single-select pill row — for short option lists
export const PillSelect = ({ label, options = [], value, onChange, testid, erp }) => (
  <div data-testid={testid}>
    <FieldLabel label={label} erp={erp} />
    <div className="flex flex-wrap gap-2">
      {options.map((o) => (
        <button
          type="button"
          key={o}
          data-testid={`${testid}-${String(o).toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
          onClick={() => onChange(value === o ? "" : o)}
          className={`rounded-full border px-4 py-2 text-xs font-bold transition-all ${
            value === o
              ? "border-[#06B6D4] bg-[#06B6D4]/15 text-[#06B6D4]"
              : "border-white/10 bg-white/[0.04] text-slate-400 hover:border-white/30 hover:text-white"
          }`}
        >
          {o}
        </button>
      ))}
    </div>
  </div>
);

// Search + autocomplete single select — for long lists (colleges etc.)
export const SearchSelect = ({ label, options = [], value, onChange, placeholder = "Search…", testid, erp }) => {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const ref = useRef(null);

  useEffect(() => {
    const close = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  const filtered = useMemo(
    () => options.filter((o) => o.toLowerCase().includes(q.toLowerCase())).slice(0, 8),
    [options, q]
  );

  return (
    <div ref={ref} className="relative" data-testid={testid}>
      <FieldLabel label={label} erp={erp} />
      <button
        type="button"
        data-testid={`${testid}-trigger`}
        onClick={() => setOpen(!open)}
        className="flex w-full items-center justify-between rounded-2xl border border-white/10 bg-[#09090B]/80 px-4 py-3 text-left text-sm outline-none focus:border-[#7C3AED]/60"
      >
        <span className={value ? "text-white" : "text-slate-600"}>{value || placeholder}</span>
        <ChevronDown className="h-4 w-4 text-slate-500" />
      </button>
      {open && (
        <div className="absolute z-30 mt-2 w-full rounded-2xl border border-white/10 bg-[#09090B] p-2 shadow-2xl">
          <input
            autoFocus
            data-testid={`${testid}-search`}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Type to search…"
            className="w-full rounded-xl border border-white/10 bg-white/[0.04] px-3 py-2 text-sm text-white placeholder:text-slate-600 outline-none"
          />
          <div className="mt-1.5 max-h-44 overflow-y-auto">
            {filtered.length === 0 && <p className="px-3 py-2 text-xs text-slate-600">No matches</p>}
            {filtered.map((o) => (
              <button
                type="button"
                key={o}
                data-testid={`${testid}-option-${o.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                onClick={() => {
                  onChange(o);
                  setOpen(false);
                  setQ("");
                }}
                className={`block w-full rounded-xl px-3 py-2 text-left text-sm transition-colors hover:bg-white/5 ${
                  value === o ? "text-[#06B6D4]" : "text-slate-300"
                }`}
              >
                {o}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// Multi-select tag chips with optional custom values
export const Chips = ({ label, options = [], values = [], onChange, testid, allowCustom = true, erp }) => {
  const [custom, setCustom] = useState("");
  const toggle = (o) => onChange(values.includes(o) ? values.filter((v) => v !== o) : [...values, o]);
  const addCustom = () => {
    const v = custom.trim();
    if (v && !values.includes(v)) onChange([...values, v]);
    setCustom("");
  };
  return (
    <div data-testid={testid}>
      <FieldLabel label={label} erp={erp} />
      <div className="flex flex-wrap gap-2">
        {[...new Set([...options, ...values])].map((o) => {
          const on = values.includes(o);
          return (
            <button
              type="button"
              key={o}
              data-testid={`${testid}-${String(o).toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              onClick={() => toggle(o)}
              className={`flex items-center gap-1.5 rounded-full border px-4 py-2 text-xs font-bold transition-all ${
                on
                  ? "border-[#7C3AED] bg-[#7C3AED]/20 text-[#A78BFA]"
                  : "border-white/10 bg-white/[0.04] text-slate-400 hover:border-white/30 hover:text-white"
              }`}
            >
              {o}
              {on && <X className="h-3 w-3" />}
            </button>
          );
        })}
        {allowCustom && (
          <span className="flex items-center gap-1">
            <input
              data-testid={`${testid}-custom`}
              value={custom}
              onChange={(e) => setCustom(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addCustom())}
              placeholder="+ Add"
              className="w-24 rounded-full border border-dashed border-white/15 bg-transparent px-3 py-2 text-xs text-white placeholder:text-slate-600 outline-none focus:border-[#06B6D4]/60"
            />
            {custom.trim() && (
              <button type="button" data-testid={`${testid}-custom-add`} onClick={addCustom} className="rounded-full bg-[#06B6D4]/15 p-2 text-[#06B6D4]">
                <Plus className="h-3 w-3" />
              </button>
            )}
          </span>
        )}
      </div>
    </div>
  );
};

export const YesNo = ({ label, value, onChange, testid, erp }) => (
  <PillSelect label={label} options={["Yes", "No"]} value={value} onChange={onChange} testid={testid} erp={erp} />
);

// Country → L1 (state/province/emirate) → L2 (district/city) → L3 (area) → Locality → auto Postal code.
// Labels come from each country's master data (State/City/ZIP for USA, Emirate/Area for UAE, …).
export const LocationCascade = ({ masters, country: countryName, value = {}, onChange, testid = "location" }) => {
  const countries = masters.locations || [];
  const country = countries.find((c) => c.country === countryName) || countries[0];
  if (!country) return null;
  const labels = country.levels || ["State", "District", "Area", "Locality", "Postal Code"];
  const states = country.states || [];

  const state = states.find((s) => s.state === value.l1);
  const districts = (state?.districts || []).map((d) => d.district);
  const district = (state?.districts || []).find((d) => d.district === value.l2);
  const areas = (district?.areas || []).map((a) => a.area);
  const area = (district?.areas || []).find((a) => a.area === value.l3);
  const localities = (area?.localities || []).map((l) => l.name);
  const locality = (area?.localities || []).find((l) => l.name === value.locality);

  const set = (patch) => onChange({ ...value, ...patch });

  const cascadeSelect = (key, label, options, next) => (
    <SearchSelect
      label={label}
      options={options}
      value={value[key] || ""}
      onChange={(v) => set({ [key]: v, ...Object.fromEntries(next.map((k) => [k, ""])) })}
      placeholder={`▼ ${label}`}
      testid={`${testid}-${key}`}
    />
  );

  return (
    <div className="sm:col-span-2" data-testid={testid}>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        {cascadeSelect("l1", labels[0], states.map((s) => s.state), ["l2", "l3", "locality", "pincode"])}
        {value.l1 && cascadeSelect("l2", labels[1], districts, ["l3", "locality", "pincode"])}
        {value.l2 && cascadeSelect("l3", labels[2], areas, ["locality", "pincode"])}
        {value.l3 && cascadeSelect("locality", labels[3], localities, ["pincode"])}
        <div>
          <FieldLabel label={labels[4] || "Postal Code"} />
          <div
            data-testid={`${testid}-pincode`}
            className="rounded-2xl border border-white/10 bg-white/[0.04] px-4 py-3 font-jbmono text-sm text-[#06B6D4]"
          >
            {locality?.pincode || "Auto-filled"}
          </div>
        </div>
      </div>
    </div>
  );
};

// FUNZI rule-based suggestions from picked values
export const FunziSuggestions = ({ form, masters, onPick }) => {
  const rules = masters.suggestion_rules || {};
  const picked = Object.values(form).join(" ").toLowerCase();
  const suggestions = [];
  Object.entries(rules).forEach(([trigger, groups]) => {
    if (picked.includes(trigger.toLowerCase())) suggestions.push(...groups);
  });
  const unique = [...new Set(suggestions)].slice(0, 4);
  if (!unique.length) return null;
  return (
    <div className="rounded-2xl border border-[#06B6D4]/25 bg-[#06B6D4]/[0.05] p-4 sm:col-span-2" data-testid="funzi-suggestions">
      <p className="flex items-center gap-2 font-jbmono text-[10px] font-bold uppercase tracking-widest text-[#06B6D4]">
        <Sparkles className="h-3.5 w-3.5" /> FUNZI suggests — tap to add
      </p>
      <div className="mt-2.5 flex flex-wrap gap-2">
        {unique.map((s) => (
          <button
            type="button"
            key={s}
            data-testid={`suggestion-${s.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
            onClick={() => onPick(s)}
            className="rounded-full border border-[#06B6D4]/40 px-4 py-2 text-xs font-bold text-[#06B6D4] transition-colors hover:bg-[#06B6D4]/15"
          >
            ✓ {s}
          </button>
        ))}
      </div>
    </div>
  );
};
